import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import 'highcharts/modules/accessibility';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PercentChange } from '../PercentChange/PercentChange';
import { Typography } from '../Typography/Typography';
import Skeleton from '../Skeleton/Skeleton';
import { buildSmallChartOptions, FundSmallChartColors } from './FundSmallChartOptions';
import { ChartData } from 'funds-model';
import Decimal from 'decimal.js';

import useStyles from './FundSmallChart.style';


interface FundSmallChartProps {
  chartData?: ChartData;
  isLoading?: boolean;
  changePercent?: Decimal;
  noChange?: boolean;
  changeRateKey?: string;
  height?: number;
  width?: number;
  className?: string;
}

const FundSmallChart: React.FC<FundSmallChartProps> = ({
  chartData,
  isLoading = false,
  changePercent,
  noChange = false,
  changeRateKey,
  height = 32,
  width = 100,
  className,
}) => {
  const c = useStyles({});
  const [t] = useTranslation('components');
  const theme = useTheme();

  const lineColor = theme.color.violets[50];
  const colors: FundSmallChartColors = useMemo(() => ({
    lineColor,
    backgroundColor: 'transparent',
  }), [lineColor]);

  const hasData = chartData && chartData.data?.length > 0;

  const options = useMemo(
    () => hasData ? buildSmallChartOptions(height, width, chartData.data, colors) : undefined,
    [hasData, height, width, chartData, colors],
  );

  if (isLoading) {
    return <Skeleton className={c.skeleton} />;
  }

  if (noChange || !hasData || !options || !changeRateKey) {
    return changeRateKey ? (
      <Typography className={c.emptyLabel}>
        {t('fund-small-chart.no-roi-for', { changeRateKey })}
      </Typography>
    ) : null;
  }

  const changeValue = changePercent?.div(100).toNumber() || 0;

  return (
    <div className={clsx(c.root, className)}>
      <PercentChange value={changeValue} />
      <div className={c.chart}>
        <HighchartsReact
          immutable
          highcharts={Highcharts}
          constructorType="stockChart"
          containerProps={{ tabIndex: 'none' }}
          options={options}
        />
      </div>
    </div>
  );
};

export { FundSmallChart };
